import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "@phosphor-icons/react";

import { cn } from "../../lib/utils";
import { cva } from "class-variance-authority";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "./resizable";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        "z-[1300]", // TODO remove once MUI is gone
        className,
      )}
      {...props}
    />
  );
}

const sheetContentVariants = cva(
  "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col shadow-lg transition ease-in-out data-[state=closed]:duration-200 data-[state=open]:duration-200",
  {
    variants: {
      side: {
        right:
          "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right right-0 border-l",
        left: "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left left-0 border-r",
        top: "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top top-0 border-b",
        bottom:
          "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom bottom-0 border-t",
      },
      expanded: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        side: ["right", "left"],
        class: "inset-y-0 h-full w-3/4 sm:max-w-sm",
      },
      {
        side: ["right", "left"],
        expanded: true,
        class: "max-w-3/4 sm:max-w-3/4",
      },
      {
        side: ["top", "bottom"],
        class: "inset-x-0 h-auto",
      },
    ],
    defaultVariants: {
      side: "right",
    },
  },
);

function SheetContent({
  className,
  children,
  side = "right",
  expanded = false,
  hideCloseButton = false,
  hideOverlay = false,
  style,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  expanded?: boolean;
  /** Hide the built-in top-right close button (e.g. when the sheet supplies its own). */
  hideCloseButton?: boolean;
  /**
   * Skip the full-screen dimming overlay. Pair with `modal={false}` on `Sheet`
   * for a non-blocking side panel that leaves the rest of the screen
   * interactive (e.g. a docked panel alongside another fixed panel).
   */
  hideOverlay?: boolean;
}) {
  return (
    <SheetPortal>
      {!hideOverlay && <SheetOverlay />}
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          sheetContentVariants({ side, expanded }),
          "z-[1300]", // TODO remove once MUI is gone
          className,
        )}
        // Right-docked sheets sit to the LEFT of the app's AI chat panel while
        // it's open (`--agent-panel-width`); a no-op (0px) when it's closed, so
        // every right slide-in coexists with the chat without per-page wiring.
        style={
          side === "right"
            ? { right: "var(--agent-panel-width, 0px)", ...style }
            : style
        }
        // Clicking into the app's right-docked AI chat panel registers as an
        // outside interaction; don't let it dismiss the sheet. Caller's handler
        // still runs for genuine outside clicks.
        onInteractOutside={(event) => {
          const target = event.detail.originalEvent.target;
          if (
            target instanceof HTMLElement &&
            target.closest("[data-agent-panel]")
          ) {
            event.preventDefault();
            return;
          }
          onInteractOutside?.(event);
        }}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetContentResizable({
  className,
  children,
  side = "right",
  resizableProps,
  style,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  /**
   * The size of the sheet in percentage of the viewport width
   */
  size?: number;
  minSize?: number;
  /** Hide the built-in top-right close button (e.g. when the sheet supplies its own). */
  hideCloseButton?: boolean;
  /** Skip the full-screen dimming overlay (see {@link SheetContent}). */
  hideOverlay?: boolean;
  resizableProps?: React.ComponentProps<typeof ResizablePanel>;
}) {
  return (
    <SheetContent
      side={side}
      className={cn(
        side === "right" || side === "left" ? "w-full" : "h-full",
        "bg-transparent border-none max-w-full sm:max-w-full shadow-none",
        className,
      )}
      // A full-width (`w-full`) resizable sheet must shrink by the chat panel's
      // width so it doesn't run under it; `SheetContent` adds the matching
      // `right` offset. Both default to 0px when the panel is closed.
      style={
        side === "right"
          ? { width: "calc(100% - var(--agent-panel-width, 0px))", ...style }
          : style
      }
      {...props}
    >
      <ResizablePanelGroup
        direction={
          side === "right" || side === "left" ? "horizontal" : "vertical"
        }
      >
        <ResizablePanel className="pointer-events-auto">
          <SheetPrimitive.Close className="h-full w-full"></SheetPrimitive.Close>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel
          {...resizableProps}
          className={cn("bg-background shadow-lg", resizableProps?.className)}
        >
          {children}
        </ResizablePanel>
      </ResizablePanelGroup>
    </SheetContent>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-3", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-3", className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetContentResizable,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
